import { Link } from "react-router-dom";
import Header from "@/components/marketing/Header";
import Footer from "@/components/marketing/Footer";
import { useLang } from "@/lib/lang";

export default function TermsPage() {
  const { t, lang } = useLang();
  return (
    <div className="min-h-screen bg-[#05050A]" data-testid="terms-page">
      <Header />
      <div className="pt-24 pb-16 max-w-4xl mx-auto px-6">
        <p className="label-mono mb-4">{t("terms_label")}</p>
        <h1 className="font-['Unbounded'] text-4xl font-black text-white mb-8">{t("terms_heading")}</h1>
        <div className="prose-custom space-y-6 text-[#A0A0AB] text-sm leading-relaxed">
          <p className="text-white text-base">Last updated: {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</p>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">1. Acceptance of Terms</h2>
            <p>By accessing or using the Fortis Digital LLC website and marketplace ("Platform"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Platform. Fortis Digital LLC ("Company", "we", "us") reserves the right to update these Terms at any time.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">2. Platform Description</h2>
            <p>Fortis Digital LLC operates a digital marketplace where creators can list and sell digital products including applications, music, videos, media content, and digital services. The Platform facilitates transactions between creators (sellers) and buyers.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">3. User Accounts</h2>
            <p>To access certain features, you must create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account. You must provide accurate, current, and complete information during registration. You must be at least 18 years of age to create an account and use the Platform.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">4. Creator Terms</h2>
            <p>Creators agree to the following:</p>
            <ul className="list-disc pl-6 space-y-2 mt-2">
              <li>All products listed must comply with applicable laws and regulations.</li>
              <li>Creators must have the legal right to sell all products listed on the Platform.</li>
              <li>Commission rates are determined by the creator's subscription tier and are subject to change with 30 days' notice.</li>
              <li>Payouts are processed upon admin approval and are subject to minimum balance requirements.</li>
              <li>Fortis Digital LLC reserves the right to remove any product that violates these Terms or applicable laws.</li>
              <li>Creators are responsible for their own tax obligations and reporting.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">5. Subscription Tiers & Pricing</h2>
            <p>Creator subscription tiers (Starter, Pro, Enterprise, Non-Profit) are billed monthly. Pricing and commission rates are as listed on our <Link to="/pricing" className="text-[#00F0FF] hover:underline">Pricing page</Link>. We reserve the right to modify pricing with 30 days' advance notice. Tier subscriptions are non-refundable.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">6. Payments & Refunds</h2>
            <p>All payments are processed securely through Stripe. Buyers may request refunds within 14 days of purchase for digital products that are materially different from their description. Fortis Digital LLC is not responsible for the quality or functionality of third-party products sold on the Platform.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">7. Intellectual Property</h2>
            <p>Creators retain ownership of their intellectual property. By listing products on the Platform, creators grant Fortis Digital LLC a non-exclusive license to display, promote, and distribute their products through the Platform. The Fortis Digital LLC name, logo, and branding are trademarks of Fortis Digital LLC.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">8. Prohibited Conduct</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Listing illegal, fraudulent, or counterfeit products.</li>
              <li>Infringing on any third party's intellectual property rights.</li>
              <li>Attempting to circumvent Platform fees or commission structures.</li>
              <li>Using the Platform for money laundering or any illegal financial activity.</li>
              <li>Harassing, threatening, or abusing other users.</li>
              <li>Attempting to gain unauthorized access to the Platform or other users' accounts.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">9. Limitation of Liability</h2>
            <p>Fortis Digital LLC provides the Platform "as is" without warranties of any kind. We are not liable for any indirect, incidental, special, or consequential damages arising from your use of the Platform. Our total liability shall not exceed the amount paid by you to us in the 12 months preceding the claim.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">10. Termination</h2>
            <p>We may terminate or suspend your account at any time for violation of these Terms. Upon termination, your right to use the Platform ceases immediately. Any pending payouts will be processed within 30 days of termination, less any amounts owed to us.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">11. Governing Law</h2>
            <p>These Terms shall be governed by the laws of the United States. Any disputes arising from these Terms shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.</p>
          </section>

          <section>
            <h2 className="font-['Unbounded'] text-xl font-bold text-white mb-3">12. Contact</h2>
            <p>For questions about these Terms, contact us at <span className="text-[#00F0FF]">info@fortisdigitalllc.com</span>.</p>
          </section>
        </div>
      </div>
      <Footer />
    </div>
  );
}
